import { InteractionOverlayProps } from "./types";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import { Flex } from "../flex";
import { mergeClasses, Text, Button } from "@fluentui/react-components";
import { SkillsDefaults } from "@/common/constants/skills";
import { useCommonStyle } from "@/common/style/commonStyle";
import {
	InteractionPanelItemDescription,
	InteractionPanelItemType,
} from "@/components/organisms/interaction/types";
import { SyntheticEvent, useCallback } from "react";
import { Copy20Regular } from "@fluentui/react-icons";

export const InteractionOverlay = (props: InteractionOverlayProps) => {
	const intl = useIntl();
	const style = useStyle();
	const commonStyle = useCommonStyle();

	const descriptionTranslationKey = useCallback(() => {
		return intl.formatMessage({
			id: `InteractionPanel.${
				InteractionPanelItemDescription[props.item.statusOnPanel]
			}`,
		});
	}, []);

	const typeTranslationKey = useCallback(() => {
		return intl.formatMessage({
			id: `Interaction.${InteractionPanelItemType[props.item.type]}`,
		});
	}, []);

	const canShowItemDebuggingId = (itemType: InteractionPanelItemType) => {
		return (
			itemType == InteractionPanelItemType.Call ||
			itemType == InteractionPanelItemType.Email ||
			itemType == InteractionPanelItemType.WhatsApp ||
			itemType == InteractionPanelItemType.Callback ||
			itemType == InteractionPanelItemType.ParkedCall ||
			itemType == InteractionPanelItemType.Voicemail ||
			itemType == InteractionPanelItemType.ParkedEmail ||
			itemType == InteractionPanelItemType.Webchat ||
			itemType == InteractionPanelItemType.ParkedWebchat ||
			itemType == InteractionPanelItemType.ParkedWhatsApp
		);
	};

	const skillsLength = props.item.skills?.length ?? 0;

	const descriptionAndQueueName = useCallback(() => {
		return [`(${descriptionTranslationKey()})`, props.item.queueName]
			.filter((x: string) => !!x)
			.join(" | ");
	}, []);

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	return (
		<div className={style.container}>
			<Flex className={style.row}>
				<Text weight="semibold" className={style.descriptionType}>
					{typeTranslationKey()}
				</Text>
				<Text className={style.descriptionAndQueueName}>
					&nbsp;{descriptionAndQueueName()}
				</Text>
			</Flex>
			<>
				{canShowItemDebuggingId(props.item.type) && (
					<Flex className={style.row}>
						<Flex
							className={mergeClasses(
								style.itemDebuggingId,
								commonStyle.semiBold
							)}
							vAlign="center"
						>
							{props.item.itemDebuggingId}
						</Flex>
						<Flex
							className={mergeClasses(
								style.itemDebuggingId,
								commonStyle.semiBold
							)}
							vAlign="center"
						>
							<Button
								appearance="transparent"
								onClick={(event: SyntheticEvent) => {
									event.stopPropagation();
									copyToClipboard(props.item.itemDebuggingId);
								}}
								icon={
									<Copy20Regular
										onClick={() => {
											copyToClipboard(props.item.itemDebuggingId);
										}}
									/>
								}
								title={intl.formatMessage({
									id: "Common.CopyToClipboard",
								})}
							/>
						</Flex>
					</Flex>
				)}
				{props.item.type !== InteractionPanelItemType.Generic &&
					props.item.isSkillBasedRoutingEnabled &&
					skillsLength > 0 && (
						<div className={style.skillsText}>
							<Text weight="semibold">
								{intl.formatMessage({ id: "InteractionOverlay.Skills" })}:
							</Text>
							<div className={style.skillsList}>
								{props.item.skills?.map((skill, index) => (
									<span
										className={
											skill.weight === SkillsDefaults.MaxSkillLevel
												? commonStyle.semiBold
												: ""
										}
									>
										{skill.name}
										{index < skillsLength - 1 ? ", " : ""}
									</span>
								))}
							</div>
						</div>
					)}
			</>
		</div>
	);
};
